import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Button,
  Chip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import { ChallengeTableChallenge } from '../../../../api/types/dtos/challenge/ChallengeDto';
import { AnswerDto } from '../../../../api/services/answer.service';
import {
  ReportCell,
  CellMapping,
  OrganizationField,
  SelectedQuestion,
  ValidationWarning,
  } from '../types/reportBuilder.types';
import { processCellValue } from '../utils/reportBuilder.utils';

interface PreviewStepProps {
  cells: ReportCell[];
  cellMappings: CellMapping[];
  selectedFields: OrganizationField[];
  selectedQuestions: SelectedQuestion[];
  challenges: ChallengeTableChallenge[];
  answersMap?: Record<string, AnswerDto[]>;
  onExportConfig: () => void;
  onImportConfig: (file: File) => void;
}

const PreviewStep: React.FC<PreviewStepProps> = ({
  cells,
  cellMappings,
  selectedFields,
  selectedQuestions,
  challenges,
  answersMap,
  onExportConfig,
  onImportConfig,
}) => {
  const previewRows = useMemo(() => {
    const sortedCells = [...cells].sort((a, b) => a.order - b.order);
    const sampleChallenges = challenges.slice(0, 20); // Preview first 20

    return sampleChallenges.map((challenge) => {
      const processedCells = sortedCells.map((cell) => {
        const mapping = cellMappings.find(m => m.cellId === cell.id);
        if (!mapping) {
          return {
            cellId: cell.id,
            value: '',
            error: 'No mapping defined',
          };
        }
        return processCellValue(challenge, mapping, answersMap);
      });

      return {
        farmId: challenge.inspectedOrgId,
        cells: processedCells,
      };
    });
  }, [cells, cellMappings, challenges]);

  const validationWarnings = useMemo((): ValidationWarning[] => {
    const warnings: ValidationWarning[] = [];

    // Check for unused fields
    const usedFields = new Set<string>();
    cellMappings.forEach(m => {
      if (m.sourceType === 'field' && m.sourceId) {
        usedFields.add(m.sourceId);
      }
    });
    selectedFields.forEach(field => {
      if (!usedFields.has(field)) {
        warnings.push({
          type: 'unused_field',
          message: `Campo "${field}" está seleccionado pero no se usa en ninguna celda`,
          itemId: field,
        });
      }
    });

    // Check for unused questions
    const usedQuestions = new Set<string>();
    cellMappings.forEach(m => {
      if (m.sourceType === 'question' && m.sourceId) {
        usedQuestions.add(m.sourceId);
      }
    });
    selectedQuestions.forEach(q => {
      const key = `${q.programVersionItemId}:${q.question.uuid}`;
      if (!usedQuestions.has(key)) {
        warnings.push({
          type: 'unused_question',
          message: `Pregunta "${q.question.text}" está seleccionada pero no se usa en ninguna celda`,
          itemId: key,
        });
      }
    });

    // Check for unmapped cells
    cells.forEach(cell => {
      const mapping = cellMappings.find(m => m.cellId === cell.id);
      if (!mapping || mapping.sourceType === 'default') {
        warnings.push({
          type: 'unmapped_cell',
          message: `Celda "${cell.name}" no tiene un mapeo definido`,
          itemId: cell.id,
        });
      }
    });

    return warnings;
  }, [cells, cellMappings, selectedFields, selectedQuestions]);

  const handleImportClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        onImportConfig(file);
      }
    };
    input.click();
  };

  const sortedCells = [...cells].sort((a, b) => a.order - b.order);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          Vista Previa del Reporte
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={handleImportClick}
          >
            Importar Configuración
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={onExportConfig}
          >
            Exportar Configuración
          </Button>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Vista previa de las primeras 20 fincas. El reporte final incluirá todas las fincas.
      </Typography>

      {validationWarnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Advertencias de validación:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {validationWarnings.map((warning, index) => (
              <li key={index}>
                <Typography variant="body2">{warning.message}</Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {previewRows.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No hay datos para mostrar. Asegúrate de que haya retos disponibles.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                {sortedCells.map((cell) => (
                  <TableCell key={cell.id} sx={{ fontWeight: 600 }}>
                    {cell.name}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {previewRows.map((row, index) => (
                <TableRow key={row.farmId}>
                  <TableCell>{index + 1}</TableCell>
                  {row.cells.map((cell) => (
                    <TableCell key={cell.cellId}>
                      {cell.error ? (
                        <Chip label={cell.error} color="error" size="small" />
                      ) : (
                        cell.value || <Typography variant="caption" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Total de fincas en el reporte: {challenges.length}
        </Typography>
      </Box>
    </Box>
  );
};

export default PreviewStep;
